/**
 * System → Audit log tab (brief §6.7.3).
 *
 * Read-only list of admin_audit_log rows. Selecting a row opens a full-page
 * detail view via `?id=` including the JSON `details` payload.
 *
 * Filters wired to the backend: actor (email exact-match), action
 * (exact-match), date range (created_at), and free-text q (substring
 * search across action / target_type / target_id / actor_email).
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Input } from '@/components/ui/input';

import { useAuditLog, useAuditLogEntry, type AuditLogFilters, type AuditLogRow } from './api';
import {
  DASH,
  DataTable,
  DataTableShell,
  DateRange,
  formatTimestamp,
  Pagination,
  toIsoEnd,
  toIsoStart,
} from './shared';

const PAGE_SIZE = 50;
const QUERY_PARAM = 'id';
const AUDIT_NOT_FOUND_MESSAGE =
  'That audit log entry may have been deleted, or the link may point to an entry ' +
  'that no longer exists.';

export function AuditLogTab() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);

  const filters: AuditLogFilters = {
    actor: actor.trim() || undefined,
    action: action.trim() || undefined,
    q: q.trim() || undefined,
    from: toIsoStart(from),
    to: toIsoEnd(to),
    limit: PAGE_SIZE,
    offset,
  };

  const { data, isLoading, error } = useAuditLog(filters);
  const rows: AuditLogRow[] = data?.data ?? [];
  const total = data?.meta.total ?? 0;

  const selectedId = searchParams.get(QUERY_PARAM);
  const selectedFromRows = useMemo(
    () => (selectedId ? (rows.find((r) => r.id === selectedId) ?? null) : null),
    [rows, selectedId],
  );
  const selectedQuery = useAuditLogEntry(selectedId);
  const selected = selectedQuery.data ?? (selectedQuery.error ? null : selectedFromRows);

  const setSelectedId = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set(QUERY_PARAM, id);
    else next.delete(QUERY_PARAM);
    setSearchParams(next, { replace: true });
  };

  if (selected) {
    return <AuditDetailPage row={selected} onBack={() => setSelectedId(null)} />;
  }

  if (selectedId) {
    if (isNotFoundError(selectedQuery.error)) {
      return (
        <AuditDetailStatePage
          title="Audit event not found"
          message={AUDIT_NOT_FOUND_MESSAGE}
          auditId={selectedId}
          onBack={() => setSelectedId(null)}
        />
      );
    }

    if (selectedQuery.error) {
      return (
        <AuditDetailStatePage
          title="Could not load audit event"
          message={getErrorMessage(selectedQuery.error)}
          auditId={selectedId}
          onBack={() => setSelectedId(null)}
        />
      );
    }

    return (
      <AuditDetailStatePage
        title="Loading audit event..."
        message="Fetching this audit log entry directly."
        auditId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Input
          placeholder="Actor email…"
          value={actor}
          onChange={(e) => {
            setActor(e.target.value);
            setOffset(0);
          }}
          className="max-w-[220px]"
        />
        <Input
          placeholder="Action (e.g. clock.set)"
          value={action}
          onChange={(e) => {
            setAction(e.target.value);
            setOffset(0);
          }}
          className="max-w-[220px]"
        />
        <Input
          placeholder="Search target / actor…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOffset(0);
          }}
          className="max-w-[280px] flex-1"
        />
        <DateRange
          from={from}
          to={to}
          onChange={({ from, to }) => {
            setFrom(from);
            setTo(to);
            setOffset(0);
          }}
        />
      </div>

      <DataTableShell
        loading={isLoading}
        error={error}
        empty={rows.length === 0}
        emptyLabel="No audit events recorded yet. Admin logins, logouts, clock controls, fixture changes, job runs, and competition creation will appear here."
      >
        <DataTable<AuditLogRow>
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => setSelectedId(r.id)}
          isRowActive={(r) => r.id === selectedId}
          columns={[
            {
              key: 'created_at',
              label: 'Timestamp',
              render: (r) => (
                <span className="font-mono text-xs">{formatTimestamp(r.created_at)}</span>
              ),
            },
            {
              key: 'actor_email',
              label: 'Actor',
              render: (r) => r.actor_email ?? DASH,
            },
            {
              key: 'action',
              label: 'Action',
              render: (r) => <span className="font-mono text-xs font-medium">{r.action}</span>,
            },
            {
              key: 'target_type',
              label: 'Target type',
              render: (r) => r.target_type ?? DASH,
            },
            {
              key: 'target_id',
              label: 'Target ID',
              render: (r) =>
                r.target_id ? <span className="font-mono text-xs">{r.target_id}</span> : DASH,
            },
            {
              key: 'details',
              label: 'Details',
              render: (r) =>
                r.details == null ? (
                  DASH
                ) : (
                  <span className="line-clamp-1 max-w-[360px] font-mono text-xs text-muted-foreground">
                    {summariseDetails(r.details)}
                  </span>
                ),
            },
          ]}
        />
        <Pagination total={total} limit={PAGE_SIZE} offset={offset} onChange={setOffset} />
      </DataTableShell>
    </div>
  );
}

function AuditDetailStatePage({
  title,
  message,
  auditId,
  onBack,
}: {
  title: string;
  message: string;
  auditId: string;
  onBack: () => void;
}) {
  return (
    <div className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="text-sm font-medium text-muted-foreground hover:text-foreground"
      >
        Back to audit log
      </button>
      <div className="rounded-md border border-dashed bg-muted/20 px-4 py-10">
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{message}</p>
        <p className="mt-4 font-mono text-xs text-muted-foreground">{auditId}</p>
      </div>
    </div>
  );
}

function AuditDetailPage({ row, onBack }: { row: AuditLogRow; onBack: () => void }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={onBack}
            className="mb-2 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            Back to audit log
          </button>
          <h2 className="font-mono text-xl font-semibold">{row.action}</h2>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{row.id}</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <FieldRow label="Timestamp" value={formatTimestamp(row.created_at)} />
        <FieldRow label="Actor email" value={row.actor_email ?? DASH} />
        <FieldRow label="Actor ID" value={row.actor_id ?? DASH} mono />
        <FieldRow label="Target type" value={row.target_type ?? DASH} />
        <FieldRow label="Target ID" value={row.target_id ?? DASH} mono />
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Details</div>
        <pre className="mt-1 whitespace-pre-wrap break-words rounded-md border bg-muted/30 p-3 text-xs">
          {row.details == null ? DASH : JSON.stringify(row.details, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function FieldRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="space-y-1 rounded-md border bg-card px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={mono ? 'font-mono text-sm' : 'text-sm'}>{value}</div>
    </div>
  );
}

/** Compact inline summary of the details JSON for the table cell. */
function summariseDetails(details: unknown): string {
  if (details == null) return DASH;
  try {
    const json = JSON.stringify(details);
    if (json.length <= 80) return json;
    return `${json.slice(0, 77)}…`;
  } catch {
    return DASH;
  }
}

function isNotFoundError(error: unknown): boolean {
  return Boolean(
    error && typeof error === 'object' && (error as { status?: unknown }).status === 404,
  );
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Request failed';
}
